import React, { useState, useEffect } from 'react';
import { Camera, User } from 'lucide-react';

interface ProfilePhotoProps {
  userName?: string;
  userEmail?: string;
  size?: 'small' | 'medium' | 'large';
  editable?: boolean;
  onPhotoChange?: (photoUrl: string) => void;
}

const ProfilePhoto: React.FC<ProfilePhotoProps> = ({
  userName = 'User',
  userEmail = '',
  size = 'small',
  editable = false,
  onPhotoChange
}) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    // Load photo from localStorage on component mount
    const loadPhoto = () => {
      const savedPhoto = localStorage.getItem('niwt_profile_photo');
      if (savedPhoto) {
        setPhotoUrl(savedPhoto);
      }
    };
    
    loadPhoto();
    
    // Listen for storage changes to update photo across components
    window.addEventListener('storage', loadPhoto);
    
    return () => {
      window.removeEventListener('storage', loadPhoto);
    };
  }, []);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      // Read and convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPhotoUrl(base64String);
        
        // Save to localStorage
        localStorage.setItem('niwt_profile_photo', base64String);
        
        // Call callback if provided
        if (onPhotoChange) {
          onPhotoChange(base64String);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-8 h-8 text-sm';
      case 'medium':
        return 'w-16 h-16 text-xl';
      case 'large':
        return 'w-32 h-32 text-3xl';
      default:
        return 'w-8 h-8 text-sm';
    }
  };

  const sizeClasses = getSizeClasses();
  const initial = userName?.[0]?.toUpperCase() || 'U';

  return (
    <div className="relative inline-block">
      {editable && (
        <input
          type="file"
          id="profile-photo-upload"
          className="hidden"
          accept="image/*"
          onChange={handlePhotoUpload}
        />
      )}
      
      <div
        className={`${sizeClasses} rounded-full overflow-hidden cursor-pointer relative`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => {
          if (editable) {
            document.getElementById('profile-photo-upload')?.click();
          }
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={userName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`${sizeClasses} bg-niwt-primary rounded-full flex items-center justify-center`}>
            <span className="text-white font-medium">
              {initial}
            </span>
          </div>
        )}
        
        {/* Upload overlay for editable mode */}
        {editable && isHovered && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center transition-opacity">
            <Camera className="w-6 h-6 text-white" />
          </div>
        )}
      </div>

      {/* Upload hint for editable mode */}
      {editable && size === 'large' && (
        <button
          type="button"
          onClick={() => document.getElementById('profile-photo-upload')?.click()}
          className="absolute bottom-0 right-0 p-2 bg-niwt-primary text-white rounded-full hover:bg-niwt-primary/90 transition-colors shadow-lg"
        >
          <Camera className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default ProfilePhoto;